import { useState } from 'react';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import { checkmarkCircle, checkmarkOutline, closeOutline, createOutline } from 'ionicons/icons';
import { Card } from './Shell';
import { PlaceSelector } from './PlaceSelector';
import { getCurrentPosition } from '../lib/tracking';
import type { Coords } from '../lib/types';

interface Props {
  /** Der Punkt, der gerade gilt - in aller Regel der vom Fahrer gesetzte. */
  current: Coords | null;
  editing: boolean;
  draft: Coords | null;
  onStartEditing: (draft: Coords | null) => void;
  onDraftChange: (coords: Coords) => void;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  onError: (message: string) => void;
}

/**
 * Abholpunkt bestätigen oder ändern - die Steuerleiste dazu. Die Karte selbst
 * gehört dem Elternscreen: er zeigt beim Bearbeiten dieselbe Kartenfläche als
 * editierbaren Punkt statt der sonstigen Fahrtübersicht, statt eine zweite
 * Karte darunter aufzuklappen.
 *
 * Wichtig ist, was hier **nicht** passiert: nichts wird automatisch
 * übernommen. Ausgangspunkt bleibt immer der Punkt, der schon gilt - der
 * eigene Standort wird nur auf ausdrückliches Antippen zum Vorschlag, und
 * gespeichert wird erst mit "Übernehmen".
 */
export function PickupEditor({ current, editing, draft, onStartEditing, onDraftChange, onConfirm, onCancel, onError }: Props) {
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);

  async function useMyPosition() {
    setLocating(true);
    try {
      const fix = await getCurrentPosition();
      onStartEditing({ lat: fix.lat, lng: fix.lng, accuracy: fix.accuracy ?? undefined });
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Standort nicht verfügbar.');
    } finally {
      setLocating(false);
    }
  }

  async function confirm() {
    setSaving(true);
    try {
      await onConfirm();
    } finally {
      setSaving(false);
    }
  }

  const changed =
    draft !== null &&
    (current === null || draft.lat !== current.lat || draft.lng !== current.lng);

  return (
    <Card>
      <h2>Dein Abholpunkt</h2>
      <p>
        {current
          ? 'Dieser Punkt gilt gerade. Passt er nicht, verschiebe ihn - übernommen wird er erst, wenn du bestätigst.'
          : 'Noch kein Punkt gesetzt. Wähle deinen Standort oder tippe auf die Karte.'}
      </p>

      <div className="r2f-row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <IonButton size="small" onClick={useMyPosition} disabled={locating || saving}>
          {locating ? <IonSpinner name="dots" /> : <IonIcon slot="start" icon={checkmarkCircle} />}
          Mein Standort
        </IonButton>
        {!editing && (
          <IonButton size="small" fill="outline" onClick={() => onStartEditing(current)}>
            <IonIcon slot="start" icon={createOutline} />
            Ändern
          </IonButton>
        )}
      </div>

      {editing && (
        <>
          <div style={{ marginTop: 12 }}>
            <PlaceSelector value={draft} onChange={onDraftChange} onError={onError} />
          </div>

          <div className="r2f-row" style={{ gap: 8, marginTop: 12 }}>
            <IonButton
              style={{ flex: 1 }}
              onClick={confirm}
              disabled={!draft || !changed || saving}
            >
              {saving ? <IonSpinner name="dots" /> : (
                <>
                  <IonIcon slot="start" icon={checkmarkOutline} />
                  Übernehmen
                </>
              )}
            </IonButton>
            <IonButton fill="clear" color="medium" disabled={saving} onClick={onCancel}>
              <IonIcon slot="start" icon={closeOutline} />
              Abbrechen
            </IonButton>
          </div>

          {!changed && draft && (
            <p className="r2f-muted" style={{ marginTop: 8 }}>
              Unverändert - verschiebe den Marker oder wähle einen anderen Ort.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
